import { getEquipmentSuffix } from "../../utils/utils";
import {
  DRIVER_EVENTS,
  Drop_Hook_Move_Page_Names,
  DROP_TRAILER_TYPE,
} from "../constants";
import {
  MAX_TRAILER_NUMBER_HOOK_LENGTH,
  TRAILER_HOOK_VALIDATION,
  DOLLY_HOOK_VALIDATION,
} from "./TrailerHook/constants";
import { ALPHANUMERIC_AND_HYPHEN_REGEX } from "../constRegex";

const isTrailerDropFieldVisible = (field, dropType, data = {}) => {
  switch (field) {
    case "selectedCustomer":
      return ![DROP_TRAILER_TYPE.TemporaryLocation, DROP_TRAILER_TYPE.AgentInterline].includes(dropType);
    case "customerName":
      return (
        data.selectedCustomer?.value === "other" &&
        ![DROP_TRAILER_TYPE.TemporaryLocation, DROP_TRAILER_TYPE.AgentInterline].includes(dropType)
      );
    case "agentDetail":
      return dropType === DROP_TRAILER_TYPE.AgentInterline;
    case "locationName":
      return dropType === DROP_TRAILER_TYPE.TemporaryLocation;
    case "receiverName":
      return [DROP_TRAILER_TYPE.CustomerDelivery, DROP_TRAILER_TYPE.AgentInterline].includes(dropType);
    default:
      return false;
  }
};

const canAddTrailer = (
  attachedTrailersCount,
  hookedTrailersCount,
  maxAllowedTrailerCount
) => {
  return (
    attachedTrailersCount >= maxAllowedTrailerCount ||
    attachedTrailersCount + hookedTrailersCount >= maxAllowedTrailerCount
  );
};

const canAddDolly = ({
  attachedDolliesCount = 0,
  hookedDolliesCount,
  maxAllowedDolliesCount
}) => {
  return (
    attachedDolliesCount >= maxAllowedDolliesCount ||
    attachedDolliesCount + hookedDolliesCount >= maxAllowedDolliesCount
  );
};

const getArrivedAtStopId = (routeData) => {
  return routeData.currentStatus?.status === DRIVER_EVENTS.ArrivedAtStop
    ? routeData.currentStatus?.stopId
    : null;
};

const getPageDetails = (pageName) => {
  return {
    isDropHookPage: pageName === Drop_Hook_Move_Page_Names.DROP_HOOK_MOVE,
    isTrailerDropFormPage: pageName === Drop_Hook_Move_Page_Names.TRAILER_DROP,
    isSelectShipmentPage: pageName === Drop_Hook_Move_Page_Names.MOVE_BILLS,
  };
};

const normalizedTrailerKey = (value) =>
  (value && String(value).trim() ? String(value).trim() : "").toUpperCase();

// True if trimmed input matches an option label/value (same trailerNumber as getTrailerOptions; suffix is labelSuffix only).
const trailerInputMatchesDropdownOption = (inputValue, options) => {
  const key = normalizedTrailerKey(inputValue);
  if (!key) return false;
  return (options || []).some((o) =>
    [o.label, o.value].filter(Boolean).some((field) => normalizedTrailerKey(field) === key)
  );
};

const getTrailerOptions = (trailerList, showTrailerScac) => {
  return trailerList.map(
    ({ trailerNumber, trailerId, trailerStatus, trailerScac, isStraightTruck }) => ({
      label: trailerNumber,
      value: trailerNumber,
      trailerStatus,
      trailerScac,
      trailerId,
      isStraightTruck,
      labelSuffix: getEquipmentSuffix({
        equipmentScac: trailerScac,
        equipmentStatus: trailerStatus,
        showTrailerScac,
      }),
    })
  );
}

const getDollyOptions = (dollyList, showTrailerScac) => {
  return dollyList.map(
    ({ dollyNumber, dollyId, dollyStatus, dollyScac }) => ({
      label: dollyNumber,
      value: dollyNumber,
      dollyStatus,
      dollyScac,
      dollyId,
      labelSuffix: getEquipmentSuffix({
        equipmentStatus: dollyStatus,
        equipmentScac: dollyScac,
        showTrailerScac,
      }),
    })
  )
}

const getNewHookedDolly = () => ({
  trailerNumber: null,
  trailerScac: null,
  dollyNumber: null,
  dollyScac: null,
  dollyStatus: null,
});

const getInitialHookStateData = () => {
  return [
    {
      hookDolly: getNewHookedDolly(),
      hookDollyError: null
    }
  ];
}


const getDollyValidationError = ({
  dollyId,
  dollyStatus,
  dollyEntries,
  getEntryDollyId,
  allowedDollyStatusesToHook,
  validationMessages,
  isDollyStatusInvalid,
}) => {
  if (!dollyId) {
    return validationMessages.required;
  }

  const selectedDollies = (dollyEntries ?? []).filter(
    (entry) => dollyId && getEntryDollyId(entry) === dollyId
  );

  if (selectedDollies.length > 1) {
    return validationMessages.duplicate;
  }

  if (isDollyStatusInvalid(dollyStatus, allowedDollyStatusesToHook)) {
    return validationMessages.invalidStatus(allowedDollyStatusesToHook);
  }

  return null;
};

const checkValidDollyNumber = ({hookDollies, dollyId, dollyStatus, allowedDollyStatusesToHook}) => {
  return getDollyValidationError({
    dollyId,
    dollyStatus,
    dollyEntries: hookDollies,
    getEntryDollyId: (dolly) => dolly.hookDolly.dollyId,
    allowedDollyStatusesToHook,
    validationMessages: {
      required: '* Required',
      duplicate: 'Duplicate dolly entry',
      invalidStatus: (allowedStatuses) =>
        `Valid dolly status: ${
          allowedStatuses.length === 0 ? 'None' : allowedStatuses.join(', ')
        }`,
    },
    isDollyStatusInvalid: (status, allowedStatuses) =>
      status && !allowedStatuses.includes(status),
  });
};

// Validate dolly number for trailer hook (used with enableDollyHook feature)
const checkValidDollyForTrailerHook = ({
  dollyId,
  dollyStatus,
  hookTrailerDollies,
  allowedDollyStatusesToHook,
}) => {
  return getDollyValidationError({
    dollyId,
    dollyStatus,
    dollyEntries: hookTrailerDollies,
    getEntryDollyId: (dolly) => dolly.dollyId,
    allowedDollyStatusesToHook,
    validationMessages: {
      required: DOLLY_HOOK_VALIDATION.REQUIRED,
      duplicate: DOLLY_HOOK_VALIDATION.DUPLICATE,
      invalidStatus: (allowedStatuses) =>
        DOLLY_HOOK_VALIDATION.INVALID_STATUS(allowedStatuses ?? []),
    },
    isDollyStatusInvalid: (status, allowedStatuses) =>
      status && !(allowedStatuses ?? []).includes(status),
  });
};

// Determine if dolly input should be shown for a trailer row based on attachedTrailersCount and index
const shouldShowDollyInput = (attachedTrailersCount, index) => {
  return attachedTrailersCount + index > 0;
};

// Get the trailer number for display label (1-indexed, considering already attached trailers)
const getTrailerDisplayNumber = (attachedTrailersCount, index) => {
  return attachedTrailersCount + index + 1;
};

const needsDollyValidation = ({
  enableDollyHook,
  isBobtailSelected,
  attachedTrailersCount,
  hookTrailersLength,
}) => {
  return enableDollyHook && !isBobtailSelected && attachedTrailersCount + hookTrailersLength > 1;
};

const hasValidDollies = ({ hookTrailerDollies, attachedTrailersCount, hookTrailersLength }) => {
  return hookTrailerDollies.every((dolly, index) => {
    const isFirstOverallTrailer = attachedTrailersCount + index === 0;
    if (isFirstOverallTrailer) return true;
    if (index >= hookTrailersLength) return true;
    return Boolean(dolly.dollyId);
  });
};

const hasDollyNoError = ({
  hookTrailerDolliesError,
  attachedTrailersCount,
  hookTrailersLength,
}) => {
  return (hookTrailerDolliesError ?? []).every((error, index) => {
    const isFirstOverallTrailer = attachedTrailersCount + index === 0;
    if (isFirstOverallTrailer) return true;
    if (index >= hookTrailersLength) return true;
    return error === null;
  });
};

const hasDollyValidationError = ({
  enableDollyHook,
  isBobtailSelected,
  attachedTrailersCount,
  hookTrailersLength,
  hookTrailerDollies,
  hookTrailerDolliesError,
}) => {
  const needsValidation = needsDollyValidation({
    enableDollyHook,
    isBobtailSelected,
    attachedTrailersCount,
    hookTrailersLength,
  });
  if (!needsValidation) return false;
  return !(
    hasValidDollies({ hookTrailerDollies, attachedTrailersCount, hookTrailersLength }) &&
    hasDollyNoError({ hookTrailerDolliesError, attachedTrailersCount, hookTrailersLength })
  );
};

const getCurrentDollies = (currentDriverRoutes) => {
  return currentDriverRoutes
    .filter((route) => route.dollyNumber)
    .map(({ dollyNumber, dollyScac }) => ({
      dollyNumber,
      dollyScac
    }));
};

const getRelaxedTrailerValidationError = ({
  trailerNumber,
  attachedTrailers,
  hookTrailers,
}) => {
  const trimmed = typeof trailerNumber === "string" ? trailerNumber.trim() : "";
  if (!trimmed) return TRAILER_HOOK_VALIDATION.REQUIRED;
  if (trimmed.length > MAX_TRAILER_NUMBER_HOOK_LENGTH) {
    return TRAILER_HOOK_VALIDATION.MAX_LENGTH;
  }
  if (!ALPHANUMERIC_AND_HYPHEN_REGEX.test(trimmed)) {
    return TRAILER_HOOK_VALIDATION.INVALID_FORMAT;
  }
  const key = normalizedTrailerKey(trimmed);
  const isAlreadyHooked = (attachedTrailers ?? []).some(
    (att) => normalizedTrailerKey(att.trailerNumber) === key
  );
  if (isAlreadyHooked) return TRAILER_HOOK_VALIDATION.ALREADY_HOOKED;
  const countSame = (hookTrailers ?? []).filter(
    (t) => normalizedTrailerKey(t.trailerNumber) === key
  ).length;
  return countSame > 1 ? TRAILER_HOOK_VALIDATION.DUPLICATE : null;
};

const getStrictTrailerValidationError = ({
  trailerNumber,
  trailerId,
  trailerStatus,
  isStraightTruck,
  attachedTrailers,
  hookTrailers,
  allowedTrailerStatusesToHook,
}) => {
  if (!trailerId) return TRAILER_HOOK_VALIDATION.REQUIRED;
  const trimmed = typeof trailerNumber === "string" ? trailerNumber.trim() : "";
  if (trimmed.length > MAX_TRAILER_NUMBER_HOOK_LENGTH) {
    return TRAILER_HOOK_VALIDATION.MAX_LENGTH;
  }
  const selectedTrailers = (hookTrailers ?? []).filter(
    (t) => trailerId && t.trailerId === trailerId
  );
  if (selectedTrailers.length > 1) return TRAILER_HOOK_VALIDATION.DUPLICATE;
  const isAlreadyHooked =
    trimmed &&
    (attachedTrailers ?? []).some(
      (att) => normalizedTrailerKey(att.trailerNumber) === normalizedTrailerKey(trimmed)
    );
  if (isAlreadyHooked) return TRAILER_HOOK_VALIDATION.ALREADY_HOOKED;
  if (trailerStatus && !allowedTrailerStatusesToHook.includes(trailerStatus)) {
    return TRAILER_HOOK_VALIDATION.INVALID_STATUS(allowedTrailerStatusesToHook);
  }
  if (isStraightTruck) return TRAILER_HOOK_VALIDATION.STRAIGHT_TRUCK;
  return null;
};

export {
  isTrailerDropFieldVisible,
  canAddTrailer,
  getArrivedAtStopId,
  getPageDetails,
  normalizedTrailerKey,
  trailerInputMatchesDropdownOption,
  getTrailerOptions,
  getDollyOptions,
  canAddDolly,
  getNewHookedDolly,
  checkValidDollyNumber,
  getCurrentDollies,
  getInitialHookStateData,
  getRelaxedTrailerValidationError,
  getStrictTrailerValidationError,
  checkValidDollyForTrailerHook,
  shouldShowDollyInput,
  getTrailerDisplayNumber,
  needsDollyValidation,
  hasValidDollies,
  hasDollyNoError,
  hasDollyValidationError,
};
